import './Booking.css'
import React, { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { addDays } from 'date-fns'

import { bookingValidation, BookingFormData } from '../types/bookingValidation'
import { BookingResponse } from '../types/bookingTypes'
import { bookingApi } from '../api/booking.api'
import BookingSuccessModal from './BookingSuccessModal'
import { PickupTimes, pickupTimesList } from '../utils'
import _ from 'lodash'

export default function Booking() {
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [currentSection, setCurrentSection] = useState(1)
  const [formData, setFormData] = useState<BookingFormData | null>(null)
  const [bookingResponse, setBookingResponse] = useState<BookingResponse | null>(null)
  const [phoneNumberAreaCode, setPhoneNumberAreaCode] = useState('')
  const [phoneNumberExchange, setPhoneNumberExchange] = useState('')
  const [phoneNumberSubscriber, setPhoneNumberSubscriber] = useState('')
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([])

  const syncPhoneNumber = (phoneNumberAreaCode: string, phoneNumberExchange: string, phoneNumberSubscriber: string) => {
    const phoneNumber = (phoneNumberAreaCode + phoneNumberExchange + phoneNumberSubscriber)
      .replace(/[^0-9]/g, '')
      .slice(0, 10)
    setValue('phoneNumber', phoneNumber, { shouldValidate: true, shouldDirty: true })
  }

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
  } = useForm<BookingFormData>({
    resolver: zodResolver(bookingValidation),
    mode: 'onBlur',
    defaultValues: {
      address: {
        province: 'BC',
        country: 'Canada',
      },
    },
  })

  const mutate = useMutation({
    mutationFn: (data: BookingFormData) => {
      return bookingApi.create(data)
    },
    onSuccess: (response: BookingResponse) => {
      setBookingResponse(response)
      setShowSuccessMessage(true)
      setShowSuccessModal(true)
    },
    onError: error => {
      console.error('API Error: ', error)
    },
  })

  useEffect(() => {
    if (showSuccessMessage) {
      const timer = setTimeout(() => {
        setShowSuccessMessage(false)
      }, 3000)

      return () => clearTimeout(timer)
    }
  }, [showSuccessMessage])

  function handleNext() {
    setCurrentSection(2)
  }

  function handlePrevious() {
    setCurrentSection(1)
  }

  const handleDateClick = async (date: Date | null) => {
    setSelectedDate(date)
    setValue('pickupDate', date ? date.toISOString() : '')
    // TODO: get available times for the date

    function isTodayValid(date: Date) {
      const today = new Date()

      if (!date) return false

      if (today.getDate() === date?.getDate() - 1 && today.getHours() > 6) {
        return false
      }

      if (today.getDate() === date?.getDate()) {
        return false
      }

      return true
    }

    if (date) {
      try {
        const bookingsTimes = await bookingApi.getAvailableTimeSlots(date.toISOString())
        const eachTimeCount = _.countBy(bookingsTimes, b => b.pickupTime)

        const availableTimeSlots = [
          PickupTimes.SEVEN_TO_NINE,
          PickupTimes.NINE_TO_ELEVEN,
          PickupTimes.ELEVEN_TO_THIRTEEN,
        ]

        const SEVEN_TO_NINE_COUNT = eachTimeCount[PickupTimes.SEVEN_TO_NINE]
        const NINE_TO_ELEVEN_COUNT = eachTimeCount[PickupTimes.NINE_TO_ELEVEN]
        const ELEVEN_TO_THIRTEEN_COUNT = eachTimeCount[PickupTimes.ELEVEN_TO_THIRTEEN]

        if (SEVEN_TO_NINE_COUNT > 3 && NINE_TO_ELEVEN_COUNT > 3 && ELEVEN_TO_THIRTEEN_COUNT > 3) {
          return setAvailableTimeSlots([])
        }

        if (SEVEN_TO_NINE_COUNT > 3 || !isTodayValid(date)) {
          availableTimeSlots.splice(availableTimeSlots.indexOf(PickupTimes.SEVEN_TO_NINE), 1)
        }

        if (NINE_TO_ELEVEN_COUNT > 3 || !isTodayValid(date)) {
          availableTimeSlots.splice(availableTimeSlots.indexOf(PickupTimes.NINE_TO_ELEVEN), 1)
        }

        if (ELEVEN_TO_THIRTEEN_COUNT > 3 || !isTodayValid(date)) {
          availableTimeSlots.splice(availableTimeSlots.indexOf(PickupTimes.ELEVEN_TO_THIRTEEN), 1)
        }

        // const isAvailable = bookingsTimes.length <= 3 ? true : false
        setAvailableTimeSlots(availableTimeSlots)
      } catch (error) {
        console.error('Error fetching available times:', error)
      }
    }
  }

  const isPhoneNumberValid = watch('phoneNumber')?.length === 10

  const hasSection1Errors = () => {
    const section1Fields = ['firstName', 'lastName', 'email', 'pickupDate', 'pickupTime']

    const hasErrors = section1Fields.some(field => errors[field as keyof typeof errors])

    const allFieldsFilled = section1Fields.every(field => {
      const value = watch(field as keyof typeof watch)
      return value && value.toString().trim() !== ''
    })

    return hasErrors || !allFieldsFilled || !isPhoneNumberValid
  }

  const onSubmit = (data: BookingFormData) => {
    setFormData(data)
    mutate.mutate(data)
  }

  return (
    <div className='Booking-main'>
      <h2>Book your Laundry</h2>
      {mutate.isError && (
        <p style={{ color: 'red' }}>{mutate.error?.message || 'Ooops! Something went wrong. Try again later.'}</p>
      )}

      <form className='Booking-form' onSubmit={handleSubmit(onSubmit)}>
        <div className='section-1' style={{ display: currentSection === 1 ? 'block' : 'none' }}>
          <div className='form-row'>
            <div className='Booking-form-group'>
              <input type='text' id='firstName' {...register('firstName')} placeholder='First Name' />
              {errors.firstName && (
                <p className='error' style={{ color: 'red', marginTop: 0 }}>
                  <small>{errors.firstName.message}</small>
                </p>
              )}
            </div>
            <div className='Booking-form-group'>
              <input type='text' id='lastName' {...register('lastName')} placeholder='Last Name' />
              {errors.lastName && (
                <p className='error' style={{ color: 'red', marginTop: 0 }}>
                  <small>{errors.lastName.message}</small>
                </p>
              )}
            </div>
          </div>

          <div className='form-row-desktop'>
            <div className='Booking-form-group'>
              <input type='hidden' {...register('phoneNumber')} />
              <div className='phoneNumberCombination'>
                <span>{'('}</span>
                <input
                  id='phoneNumberAreaCode'
                  name='phoneNumberAreaCode'
                  autoComplete='tel-area-code'
                  inputMode='numeric'
                  pattern='[0-9]*'
                  maxLength={3}
                  value={phoneNumberAreaCode}
                  onChange={e => {
                    const areaCode = e.target.value.replace(/[^0-9]/g, '').slice(0, 3)
                    setPhoneNumberAreaCode(areaCode)
                    syncPhoneNumber(areaCode, phoneNumberExchange, phoneNumberSubscriber)
                  }}
                  onKeyUp={e => {
                    e.currentTarget.value.length === 3 && document.getElementById('phoneNumberExchange')?.focus()
                  }}
                  placeholder='123'
                />
                <span>{') '}</span>
                <input
                  id='phoneNumberExchange'
                  name='phoneNumberExchange'
                  autoComplete='tel-local-prefix'
                  inputMode='numeric'
                  pattern='[0-9]*'
                  maxLength={3}
                  value={phoneNumberExchange}
                  onChange={e => {
                    const exchange = e.target.value.replace(/[^0-9]/g, '').slice(0, 3)
                    setPhoneNumberExchange(exchange)
                    syncPhoneNumber(phoneNumberAreaCode, exchange, phoneNumberSubscriber)
                  }}
                  onKeyUp={e => {
                    e.currentTarget.value.length === 3 && document.getElementById('phoneNumberSubscriber')?.focus()
                  }}
                  placeholder='456'
                />
                <span> - </span>
                <input
                  id='phoneNumberSubscriber'
                  name='phoneNumberSubscriber'
                  autoComplete='tel-local-suffix'
                  inputMode='numeric'
                  pattern='[0-9]*'
                  maxLength={4}
                  value={phoneNumberSubscriber}
                  onChange={e => {
                    const Subscriber = e.target.value.replace(/[^0-9]/g, '').slice(0, 4)
                    setPhoneNumberSubscriber(Subscriber)
                    syncPhoneNumber(phoneNumberAreaCode, phoneNumberExchange, Subscriber)
                  }}
                  placeholder='7890'
                />
              </div>
              {errors.phoneNumber && (
                <p className='error' style={{ color: 'red', marginTop: 0 }}>
                  <small>{errors.phoneNumber.message}</small>
                </p>
              )}
            </div>
            <div className='Booking-form-group'>
              <input type='text' id='email' {...register('email')} placeholder='example@email.com' />
              {errors.email && (
                <p className='error' style={{ color: 'red', marginTop: 0 }}>
                  <small>{errors.email.message}</small>
                </p>
              )}
            </div>
          </div>
          <div className='pickup-row'>
            <div className='Booking-form-group'>
              {/* <label htmlFor='pickupDate'>Pickup Date</label> */}
              <DatePicker
                selected={selectedDate}
                onChange={handleDateClick}
                inline
                minDate={new Date()}
                maxDate={addDays(new Date(), 14)}
                filterDate={date => [0, 3, 6].includes(date.getDay())}
                dateFormat='yyyy-MM-dd'
              />
              {errors.pickupDate && (
                <p className='error' style={{ color: 'red', marginTop: 0 }}>
                  <small>{errors.pickupDate.message}</small>
                </p>
              )}
            </div>

            <div className='Booking-form-group'>
              {/* <label>Pickup Time</label> */}
              <div className='pickup-time-options'>
                {availableTimeSlots.map(timeOption => (
                  <label key={timeOption} className='pickup-time-option'>
                    <input type='radio' value={timeOption} {...register('pickupTime')} />
                    <span>{timeOption}</span>
                  </label>
                ))}
              </div>
              {errors.pickupTime && (
                <p className='error' style={{ color: 'red', marginTop: 0 }}>
                  <small>{errors.pickupTime.message}</small>
                </p>
              )}
            </div>
          </div>

          <button type='button' className='Booking-next' onClick={handleNext} disabled={hasSection1Errors()}>
            Next
          </button>
        </div>

        <div className='section-2' style={{ display: currentSection === 2 ? 'block' : 'none' }}>
          <div className='Booking-form-group'>
            <input type='text' id='streetAddress' {...register('address.streetAddress')} placeholder='Street Address' />
            {errors.address?.streetAddress && (
              <p className='error' style={{ color: 'red', marginTop: 0 }}>
                <small>{errors.address.streetAddress.message}</small>
              </p>
            )}
          </div>

          <div className='form-row'>
            <div className='Booking-form-group'>
              <input type='text' id='city' {...register('address.city')} placeholder='City' />
              {errors.address?.city && (
                <p className='error' style={{ color: 'red', marginTop: 0 }}>
                  <small>{errors.address.city.message}</small>
                </p>
              )}
            </div>

            <div className='Booking-form-group'>
              <input type='text' id='province' {...register('address.province')} placeholder='Province' disabled />
              {errors.address?.province && (
                <p className='error' style={{ color: 'red', marginTop: 0 }}>
                  <small>{errors.address.province.message}</small>
                </p>
              )}
            </div>
          </div>

          <div className='form-row'>
            <div className='Booking-form-group'>
              <input
                type='text'
                id='postalCode'
                {...register('address.postalCode')}
                placeholder='Postal Code'
                maxLength={6}
              />
              {errors.address?.postalCode && (
                <p className='error' style={{ color: 'red', marginTop: 0 }}>
                  <small>{errors.address.postalCode.message}</small>
                </p>
              )}
            </div>

            <div className='Booking-form-group'>
              <input type='text' id='country' {...register('address.country')} placeholder='Country' disabled />
              {errors.address?.country && (
                <p className='error' style={{ color: 'red', marginTop: 0 }}>
                  <small>{errors.address.country.message}</small>
                </p>
              )}
            </div>
          </div>

          <div className='Booking-form-group'>
            <textarea
              id='description'
              {...register('description')}
              rows={4}
              placeholder='Tell us more about your request...'
              maxLength={200}
            />
            {errors.description && (
              <p className='error' style={{ color: 'red', marginTop: 0 }}>
                <small>{errors.description.message}</small>
              </p>
            )}
          </div>

          <div className='button-group'>
            <button type='button' className='Booking-previous' onClick={handlePrevious}>
              Back
            </button>
            <button type='submit' className='Booking-submit' disabled={mutate.isPending}>
              {mutate.isPending ? 'Sending...' : showSuccessMessage ? 'Requested successfully!' : 'Send'}
            </button>
          </div>
        </div>
      </form>
      <BookingSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        reload={() => {
          window.location.reload()
        }}
        phoneNumber={formData?.phoneNumber as unknown as string}
        orderNumber={bookingResponse?.orderNumber as unknown as string}
        email={bookingResponse?.email as unknown as string}
      />
    </div>
  )
}
